/**
 * Continuous level-of-detail (LOD) band signals exposed to widgets as CSS
 * vars. Each maps a range of viewport-scale values to a 0..1 progression via
 * smoothstep, so widgets can drive grow / font-size / opacity continuously
 * without the renderer dictating layout. Widget authors mark internals with
 * `header` / `body` / `details` attributes and reference these vars to make
 * sections expand-into-available-space as zoom changes.
 *
 * Bands are tied to the effective render scale (vp.zoom at the focal level,
 * attenuated by container fit-scales for nested depth). All items at a given
 * focal level reveal in unison as the user zooms — predictable across cards of
 * varying model widths — and children inside a fitted-down container stay
 * collapsed until the user dives further in (their effective scale is
 * fractioned).
 *
 * Each band has two phases so the zoom transition feels graceful:
 *   - Structure phase (e.g. `--arch-band-compact`) ramps first. Drives grow
 *     and font-size so the layout opens up to make room for content before any
 *     new content shows.
 *   - Reveal phase (e.g. `--arch-band-compact-reveal`) ramps after, completing
 *     at the same final scale. Drives opacity of newly-introduced content, so
 *     things fade in only once positions have settled. Avoids the "content
 *     appears, then everything moves" visual that mixing both onto a single var
 *     produces.
 *
 * Centralised here so module widgets, edge widgets and TypesMode previews all
 * produce identical band values at a given scale.
 */
// Placed so nested detail (subspaces, descriptions) is FULLY visible by 0.5 —
// half zoom already reads as "I'm looking at this level's content".
export const BAND_COMPACT_START         = 0.42; // body slot starts opening
export const BAND_COMPACT_END           = 0.46; // body slot fully open
export const BAND_COMPACT_REVEAL_START  = 0.46; // body opacity starts revealing
export const BAND_COMPACT_REVEAL_END    = 0.5;  // body fully visible
export const BAND_FULL_START            = 1.09; // details slot starts opening
export const BAND_FULL_END              = 1.13; // details slot fully open
export const BAND_FULL_REVEAL_START     = 1.13; // details opacity starts revealing
export const BAND_FULL_REVEAL_END       = 1.15; // details fully visible

export function smoothstep(a: number, b: number, x: number) {
  if (x <= a) return 0;
  if (x >= b) return 1;
  const t = (x - a) / (b - a);
  return t * t * (3 - 2 * t);
}

/**
 * Compute the CSS custom properties widgets read for continuous LOD.
 * Centralised so module widgets, edge widgets and TypesMode previews all
 * produce identical band values at a given scale.
 *
 * (`--arch-projected-width` used to ride along here; it had zero consumers
 * across the repo + stdlib and was dropped — one less per-item style write
 * per zoom step.)
 */
export function bandCssVars(scale: number): React.CSSProperties {
  const bandCompact = smoothstep(BAND_COMPACT_START, BAND_COMPACT_END, scale);
  const bandCompactReveal = smoothstep(
    BAND_COMPACT_REVEAL_START,
    BAND_COMPACT_REVEAL_END,
    scale,
  );
  const bandFull = smoothstep(BAND_FULL_START, BAND_FULL_END, scale);
  const bandFullReveal = smoothstep(
    BAND_FULL_REVEAL_START,
    BAND_FULL_REVEAL_END,
    scale,
  );
  return {
    ...({
      "--arch-scale": `${scale}`,
      "--arch-band-compact": `${bandCompact}`,
      "--arch-band-compact-reveal": `${bandCompactReveal}`,
      "--arch-band-full": `${bandFull}`,
      "--arch-band-full-reveal": `${bandFullReveal}`,
    } as React.CSSProperties),
  };
}
